import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { Activity, TrendingDown, TrendingUp, Users } from "tabler-icons-react";

dayjs.locale("fr");
dayjs.extend(isBetween);

const EngagementMetricCard = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
}) => (
  <Card padding="md" radius="md" withBorder>
    <Group position="apart" mb="xs">
      <ThemeIcon color={color} variant="light" size="lg">
        {icon}
      </ThemeIcon>
      {trend && (
        <Badge
          variant="light"
          color={trend > 0 ? "green" : "red"}
          leftSection={
            trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />
          }
        >
          {Math.abs(trend)}%
        </Badge>
      )}
    </Group>
    <Text size="xs" color="dimmed" transform="uppercase" weight={600} mb={4}>
      {title}
    </Text>
    <Text size="xl" weight={700} mb={4}>
      {value}
    </Text>
    {subtitle && (
      <Text size="xs" color="dimmed">
        {subtitle}
      </Text>
    )}
  </Card>
);

const ParentEngagementChart = ({
  parentsData,
  isLoading,
  dateRange,
  provenanceFilter = "all",
}) => {
  // Calcul des métriques d'engagement
  const engagementMetrics = useMemo(() => {
    if (!parentsData?.data || !dateRange) {
      return {
        totalParents: 0,
        active7Days: 0,
        active30Days: 0,
        withActivity: 0,
        neverActive: 0,
        engagementRate7: 0,
        engagementRate30: 0,
        activityRate: 0,
      };
    }
    // Utiliser la fin de la période sélectionnée comme référence
    const { start, end } = dateRange;
    const now = dayjs();
    // éviter d'utiliser une date de fin future (ex: endOf('month') dans le futur)
    const referenceEnd = end.isAfter(now) ? now : end;
    const sevenDaysAgo = referenceEnd.clone().subtract(7, "days");
    const thirtyDaysAgo = referenceEnd.clone().subtract(30, "days");

    // Filtrer TOUS les parents par provenance uniquement (on garde les parents même s'ils se sont inscrits avant la période)
    const filteredParents = parentsData.data.filter((parent) => {
      // Filtrer par provenance si un filtre est appliqué
      const matchesProvenance =
        provenanceFilter === "all" ||
        (parent.provenance &&
          parent.provenance.toLowerCase() === provenanceFilter.toLowerCase());

      return matchesProvenance;
    });

    const totalParents = filteredParents.length;

    // Parents actifs dans les 7 derniers jours (utilisation de crédit OU connexion)
    // Actifs dans les 7 jours précédant la fin de la période (inclus)
    const active7Days = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      const creditActive =
        lastCreditUsage &&
        lastCreditUsage.isBetween(sevenDaysAgo, referenceEnd, "day", "[]");
      const loginActive =
        lastLogin &&
        lastLogin.isBetween(sevenDaysAgo, referenceEnd, "day", "[]");

      return creditActive || loginActive;
    }).length;

    // Parents actifs dans les 30 derniers jours (utilisation de crédit OU connexion)
    // Actifs dans les 30 jours précédant la fin de la période (inclus)
    const active30Days = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      const creditActive =
        lastCreditUsage &&
        lastCreditUsage.isBetween(thirtyDaysAgo, referenceEnd, "day", "[]");
      const loginActive =
        lastLogin &&
        lastLogin.isBetween(thirtyDaysAgo, referenceEnd, "day", "[]");

      return creditActive || loginActive;
    }).length;

    // Parents avec au moins une activité (crédit ou connexion à n'importe quel moment)
    // Parents avec au moins une activité enregistrée jusqu'à la fin de la période
    const withActivity = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      // considérer l'activité si elle a eu lieu à une date <= 'referenceEnd'
      const activityBeforeEnd =
        (lastCreditUsage &&
          (lastCreditUsage.isBefore(referenceEnd, "day") ||
            lastCreditUsage.isSame(referenceEnd, "day"))) ||
        (lastLogin &&
          (lastLogin.isBefore(referenceEnd, "day") ||
            lastLogin.isSame(referenceEnd, "day")));

      return Boolean(activityBeforeEnd);
    }).length;

    // Parents qui ne se sont jamais connectés ou n'ont jamais utilisé de crédit
    const neverActive = totalParents - withActivity;

    // Taux d'engagement
    const engagementRate7 =
      totalParents > 0 ? ((active7Days / totalParents) * 100).toFixed(1) : 0;
    const engagementRate30 =
      totalParents > 0 ? ((active30Days / totalParents) * 100).toFixed(1) : 0;
    const activityRate =
      totalParents > 0 ? ((withActivity / totalParents) * 100).toFixed(1) : 0;

    return {
      totalParents,
      active7Days,
      active30Days,
      withActivity,
      neverActive,
      engagementRate7: parseFloat(engagementRate7),
      engagementRate30: parseFloat(engagementRate30),
      activityRate: parseFloat(activityRate),
    };
  }, [parentsData, provenanceFilter, dateRange]);

  // Formater la période pour l'affichage
  const getPeriodLabel = () => {
    if (!dateRange) return "";
    const { start, end } = dateRange;

    // Si c'est aujourd'hui, afficher "Aujourd'hui"
    const now = dayjs();
    if (start.isSame(now, "day") && end.isSame(now, "day")) {
      return "Aujourd'hui";
    }

    // Si c'est la semaine en cours
    if (
      start.isSame(now.startOf("week"), "day") &&
      end.isSame(now.endOf("week"), "day")
    ) {
      return "Cette semaine";
    }

    // Si c'est le mois en cours
    if (
      start.isSame(now.startOf("month"), "day") &&
      end.isSame(now.endOf("month"), "day")
    ) {
      return "Ce mois";
    }

    // Si c'est l'année en cours
    if (
      start.isSame(now.startOf("year"), "day") &&
      end.isSame(now.endOf("year"), "day")
    ) {
      return "Cette année";
    }

    // Période personnalisée
    return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
  };

  if (isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
        <Box
          h={400}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader size="lg" />
        </Box>
      </Card>
    );
  }

  return (
    <Stack spacing="md">
      {/* En-tête avec période */}
      {/* <Card padding="md" radius="md" withBorder bg="blue.0">
        <Group position="apart">
          <Box>
            <Text
              size="xs"
              color="dimmed"
              weight={600}
              transform="uppercase"
              mb={4}
            >
              Période d'analyse
            </Text>
            <Text size="lg" weight={700} color="blue.7">
              {getPeriodLabel()}
            </Text>
          </Box>
          {provenanceFilter !== "all" && (
            <Badge variant="filled" color="green" size="lg">
              {provenanceFilter}
            </Badge>
          )}
        </Group>
      </Card> */}

      {/* Métriques clés */}
      <SimpleGrid
        cols={4}
        spacing="md"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        <EngagementMetricCard
          title="Total Parents"
          value={engagementMetrics.totalParents}
          subtitle="Nombre total de parents"
          icon={<Users size={20} />}
          color="blue"
        />
        <EngagementMetricCard
          title="Actifs 7 jours"
          value={`${engagementMetrics.engagementRate7}%`}
          subtitle={`${engagementMetrics.active7Days} parents actifs`}
          icon={<Activity size={20} />}
          color="green"
        />
        <EngagementMetricCard
          title="Actifs 30 jours"
          value={`${engagementMetrics.engagementRate30}%`}
          subtitle={`${engagementMetrics.active30Days} parents actifs`}
          icon={<TrendingUp size={20} />}
          color="cyan"
        />
        <EngagementMetricCard
          title="Jamais actifs"
          value={`${(100 - engagementMetrics.activityRate).toFixed(1)}%`}
          subtitle={`${engagementMetrics.neverActive} parents`}
          icon={<TrendingDown size={20} />}
          color="red"
        />
      </SimpleGrid>

      {/* Barre de progression globale tout en un */}
      <Card padding="lg" radius="md" withBorder>
        <Group position="apart" mb="md">
          <Box>
            <Text size="lg" weight={700} mb={4}>
              Vue d'ensemble de l'engagement
            </Text>
            <Text size="sm" color="dimmed">
              Répartition de l'activité des parents inscrits
            </Text>
          </Box>
          <Badge variant="light" color="blue" size="lg">
            {engagementMetrics.totalParents} PARENTS
          </Badge>
        </Group>

        {/* Barre de progression unique avec 3 sections */}
        <Box>
          <Progress
            size="xl"
            radius="md"
            sections={[
              {
                value: engagementMetrics.engagementRate7,
                color: "green",
                label: `${engagementMetrics.engagementRate7}%`,
                tooltip: `Actifs derniers 7 jours: ${engagementMetrics.active7Days} parents`,
              },
              {
                value:
                  engagementMetrics.engagementRate30 -
                  engagementMetrics.engagementRate7,
                color: "blue",
                label: `${(engagementMetrics.engagementRate30 - engagementMetrics.engagementRate7).toFixed(1)}%`,
                tooltip: `Actifs 8-30 jours: ${engagementMetrics.active30Days - engagementMetrics.active7Days} parents`,
              },
              {
                value: 100 - engagementMetrics.engagementRate30,
                color: "red",
                label: `${(100 - engagementMetrics.engagementRate30).toFixed(1)}%`,
                tooltip: `Jamais actifs: ${engagementMetrics.neverActive} parents`,
              },
            ]}
          />

          {/* Légende sous la barre */}
          <Group position="apart" mt="lg" spacing="xl">
            <Box>
              <Group spacing={8} mb={4}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#10b981",
                    borderRadius: 3,
                  }}
                />
                <Text size="sm" weight={600}>
                  Actifs derniers 7 jours
                </Text>
              </Group>
              <Text size="lg" weight={700} color="green" mb={2}>
                {engagementMetrics.engagementRate7}%
              </Text>
              <Text size="xs" color="dimmed">
                {engagementMetrics.active7Days} parents sur{" "}
                {engagementMetrics.totalParents}
              </Text>
            </Box>

            <Box>
              <Group spacing={8} mb={4}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#3b82f6",
                    borderRadius: 3,
                  }}
                />
                <Text size="sm" weight={600}>
                  Actifs derniers 30 jours
                </Text>
              </Group>
              <Text size="lg" weight={700} color="blue" mb={2}>
                {engagementMetrics.engagementRate30}%
              </Text>
              <Text size="xs" color="dimmed">
                {engagementMetrics.active30Days} parents sur{" "}
                {engagementMetrics.totalParents}
              </Text>
            </Box>

            <Box>
              <Group spacing={8} mb={4}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#06b6d4",
                    borderRadius: 3,
                  }}
                />
                <Text size="sm" weight={600}>
                  Ont déjà été actifs
                </Text>
              </Group>
              <Text size="lg" weight={700} color="cyan" mb={2}>
                {engagementMetrics.activityRate}%
              </Text>
              <Text size="xs" color="dimmed">
                {engagementMetrics.withActivity} parents ont utilisé la
                plateforme
              </Text>
            </Box>
          </Group>
        </Box>
      </Card>
    </Stack>
  );
};

export default ParentEngagementChart;
